// src/pages/Users/Techniques/components/TechniqueModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaTimes, FaVideo, FaVolumeUp } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toAbsoluteTechniquePhotoUrl } from "../../../services/techniquesPhotoServices";

const FALLBACK_IMG = "/img/no-image.jpg";

function beltClasses(ceinture = "") {
  switch (String(ceinture).toLowerCase()) {
    case "jaune":
      return "bg-yellow-400 text-black";
    case "orange":
      return "bg-orange-400 text-black";
    case "verte":
      return "bg-green-400 text-black";
    case "bleue":
      return "bg-blue-500 text-white";
    case "marron":
      return "bg-amber-900 text-white";
    case "noire":
      return "bg-neutral-900 text-white";
    default:
      return "bg-slate-300 text-slate-900";
  }
}

// Dégradé header selon ceinture (foncé -> clair)
function headerGradient(ceinture = "") {
  switch (String(ceinture).toLowerCase()) {
    case "jaune":
      return "from-amber-700 via-amber-600 to-amber-400";
    case "orange":
      return "from-orange-800 via-orange-600 to-orange-400";
    case "verte":
      return "from-emerald-800 via-emerald-600 to-emerald-400";
    case "bleue":
      return "from-blue-900 via-blue-700 to-blue-500";
    case "marron":
      return "from-amber-900 via-amber-800 to-amber-600";
    case "noire":
      return "from-neutral-900 via-neutral-800 to-neutral-600";
    default:
      return "from-slate-700 via-slate-600 to-slate-400";
  }
}

function onImgError(e) {
  if (e?.currentTarget) e.currentTarget.src = FALLBACK_IMG;
}

// ---- TTS helpers ----
const hasJPChars = (s = "") => /[\u3040-\u30FF\u4E00-\u9FFF]/.test(s);

function pickVoice(langStartsWith = "ja", fallbackLang = "ja-JP") {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return { voice: null, lang: fallbackLang };
  }
  const voices = window.speechSynthesis.getVoices?.() || [];
  const v = voices.find((vv) =>
    vv.lang?.toLowerCase().startsWith(langStartsWith)
  );
  return { voice: v || null, lang: v?.lang || fallbackLang };
}

function speak(text, langHint = "ja", fallbackLang = "ja-JP") {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis || !text)
      return;
    const { voice, lang } = pickVoice(langHint, fallbackLang);
    const utt = new SpeechSynthesisUtterance(text);
    if (voice) utt.voice = voice;
    utt.lang = lang;
    utt.rate = 1;
    utt.pitch = 1;
    window.speechSynthesis.speak(utt);
  } catch {}
}

export default function TechniqueModal({ technique, onClose }) {
  const { image1_url, nom, traduction, famille, ceinture, video_url } =
    technique || {};

  // Import dynamique de wanakana (toKana)
  const [toKanaFn, setToKanaFn] = useState(null);
  const triedImport = useRef(false);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    if (triedImport.current) return;
    triedImport.current = true;
    (async () => {
      try {
        const mod = await import("wanakana");
        if (mod?.toKana) setToKanaFn(() => mod.toKana);
      } catch {
        // pas grave: on lira le texte tel quel
      }
    })();
  }, []);

  // Texte japonais à prononcer (romaji -> kana si possible)
  const jpForTTS = useMemo(() => {
    const jp = String(nom || "");
    if (!jp) return "";
    if (hasJPChars(jp)) return jp;
    const cleaned = jp.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [nom, toKanaFn]);

  const openVideo = () => {
    if (!video_url) return;
    window.open(video_url, "_blank", "noopener,noreferrer");
  };

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // URL de l'image (ou fallback), utilisation de toAbsoluteTechniquePhotoUrl
  // pour gérer les URLs relatives /uploads/…
  // (mais pas pour le fallback)
  const imgSrc = toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_IMG;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`Fiche technique : ${nom || ""}`}
    >
      <div className="w-full max-w-xl rounded-2xl overflow-hidden shadow-xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800">
        {/* Header dégradé par ceinture */}
        <div
          className={`px-4 py-3 bg-gradient-to-r ${headerGradient(
            ceinture
          )} text-white flex items-center justify-between`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`badge ${beltClasses(ceinture)} text-[10px] shrink-0`}
            >
              {ceinture || "?"}
            </span>
            <h3 className="font-semibold truncate">{nom}</h3>
          </div>

          {/* Actions header : TTS JP + Fermer */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => speak(jpForTTS, "ja", "ja-JP")}
              title="Prononcer (japonais)"
              aria-label="Prononcer le nom en japonais"
              type="button"
              className="w-9 h-9 grid place-items-center rounded-full ring-1 ring-white/60 text-white hover:bg-white/20 transition focus:outline-none focus:ring-2 focus:ring-white/80"
            >
              <FaVolumeUp className="text-base" />
            </button>
            <button
              onClick={onClose}
              title="Fermer"
              aria-label="Fermer la modale"
              className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div
            className="w-full bg-slate-50 dark:bg-slate-800 rounded-lg overflow-hidden"
            style={{ aspectRatio: "551 / 617" }}
          >
            <img
              src={imgSrc}
              alt={nom}
              className="w-full h-full object-contain"
              onError={onImgError}
            />
          </div>

          <div className="space-y-1">
            {traduction && (
              <p className="text-sm italic text-slate-700 dark:text-slate-300">
                {traduction}
              </p>
            )}
            {famille && (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Famille : {famille}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            {video_url && (
              <Button
                text="Vidéo"
                onClick={openVideo}
                icon={FaVideo}
                hoverLabel="Voir la vidéo"
                className="gap-2"
              />
            )}
            <Button
              text="Fermer"
              onClick={onClose}
              icon={FaTimes}
              hoverLabel="Fermer la modale"
              className="gap-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
